import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  Chip,
  Box,
  makeStyles,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  IconButton,
  MenuItem,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import CloseIcon from '@material-ui/icons/Close';
import ScheduleIcon from '@material-ui/icons/Schedule';
import RefreshIcon from '@material-ui/icons/Refresh';
import InfoIcon from '@material-ui/icons/Info';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useArgoCDAppParams } from '../../argocd-enhanced/hooks/useArgoCDAppParams';
import { useArgocdConfig } from '../../argocd-enhanced/hooks/useArgocdConfig';
import { useTektonRefresh } from '../shared/TektonRefreshContext';
import { PipelineRunsTable } from '../shared/PipelineRunsTable';
import { useTektonPipelines, WORKFLOW_TYPE, useOwnershipCheck } from '../shared';
import { usePipelineCardStyles } from '../shared/pipelineCardStyles';
import { oidcAuthApiRef } from '../../../apis';
import {
  generatePipelineRunName,
  getTektonApiVersion,
} from '../types';

// 本地样式 - 仅包含 Dialog 相关和其他特有样式
const useLocalStyles = makeStyles(theme => ({
  statusChip: {
    fontWeight: 'bold',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  pipelineIcon: {
    color: theme.palette.primary.main,
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    marginTop: 0,
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
  },
  parameterForm: {
    minWidth: 500,
    padding: theme.spacing(2),
  },
  lastUsedChip: {
    margin: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  parameterField: {
    marginBottom: theme.spacing(2),
  },
  runsSection: {
    marginTop: theme.spacing(4),
  },
}));

// Tekton Pipeline 数据结构 - 基于 Kubernetes 原生 API
interface TektonPipeline {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp: string;
    uid?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    params?: Array<{
      name: string;
      type?: string;
      default?: string | string[];
      description?: string;
    }>;
    tasks: Array<{
      name: string;
      taskRef?: {
        name: string;
        kind?: string;
      };
    }>;
  };
}

// PipelineRun 数据结构用于获取最近运行状态
interface TektonPipelineRun {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
  };
  spec: {
    pipelineRef?: {
      name: string;
    };
  };
  status?: {
    conditions?: Array<{
      status: string;
      type: string;
      reason?: string;
    }>;
    startTime?: string;
    completionTime?: string;
  };
}

// Git 信息提取函数 - 支持多种注解源
const extractGitInfo = (annotations?: Record<string, string>): {
  gitRepo?: string;
  gitBranch?: string;
  gitDisplayName?: string;
} => {
  if (!annotations) return {};

  // 优先级顺序的注解检查
  const gitSources = [
    // 1. Tekton 标准注解
    {
      url: annotations['tekton.dev/git-url'],
      branch: annotations['tekton.dev/git-branch'],
    },
    // 2. ArgoCD 相关注解（如果通过 ArgoCD sync 获取到）
    {
      url: annotations['argocd.argoproj.io/git-url'] || annotations['app.kubernetes.io/source-repo'],
      branch: annotations['argocd.argoproj.io/git-branch'] || annotations['app.kubernetes.io/source-branch'],
    },
    // 3. Backstage 源位置注解
    {
      url: annotations['backstage.io/source-location']?.replace('url:', ''),
      branch: annotations['backstage.io/source-branch'],
    },
    // 4. 通用 Git 注解
    {
      url: annotations['git.repository'] || annotations['source.repo'],
      branch: annotations['git.branch'] || annotations['source.branch'],
    },
  ];

  // 找到第一个有效的 Git 信息
  for (const source of gitSources) {
    if (source.url) {
      return {
        gitRepo: source.url,
        gitBranch: source.branch || 'main',
        gitDisplayName: source.url.split('/').pop()?.replace('.git', ''),
      };
    }
  }

  return {};
};

// 数据转换函数：将 Kubernetes Pipeline 转换为显示格式
const transformPipeline = (
  pipeline: TektonPipeline,
  lastRun?: TektonPipelineRun,
  componentName?: string, // Entity 的 Component 名称
): {
  name: string;
  displayName: string; // 用于显示的名称，格式: {action}-{component}
  action: string; // 动作类型，如 deploy, restart, rollback 等
  component: string; // 组件名称
  namespace: string;
  gitRepo?: string;
  gitBranch?: string;
  gitDisplayName?: string;
  applicationName?: string;
  params: Array<{ name: string; type?: string; default?: string | string[]; description?: string }>;
  createdAt: string;
  lastRun?: {
    name: string;
    status: 'Succeeded' | 'Running' | 'Failed' | 'Pending';
    startTime: string;
    completionTime?: string;
  };
} => {
  const getRunStatus = (run?: TektonPipelineRun): 'Succeeded' | 'Running' | 'Failed' | 'Pending' => {
    if (!run?.status?.conditions || run.status.conditions.length === 0) return 'Pending';

    const succeededCondition = run.status.conditions.find(c => c.type === 'Succeeded');
    if (!succeededCondition) return 'Pending';

    // 成功
    if (succeededCondition.status === 'True') return 'Succeeded';

    // 失败
    if (succeededCondition.status === 'False') return 'Failed';

    // Unknown 状态需要进一步判断
    if (succeededCondition.status === 'Unknown') {
      // 检查 reason 字段来区分 Running 和 Pending
      const reason = succeededCondition.reason || '';
      if (reason === 'Running' || reason === 'Started') {
        return 'Running';
      }
      return 'Pending';
    }

    return 'Pending';
  };

  // 提取 Git 信息
  const gitInfo = extractGitInfo(pipeline.metadata.annotations);

  // 提取 ArgoCD Application 信息
  const applicationName = pipeline.metadata.annotations?.['argocd.argoproj.io/application'] ||
    pipeline.metadata.annotations?.['app.kubernetes.io/part-of'] ||
    pipeline.metadata.labels?.['app.kubernetes.io/part-of'];

  // 从 Pipeline 名称中提取 action
  // 格式: {xxx}-{action}-definition-tcl
  // 例如: backstage-deploy-definition-tcl -> action = deploy
  const pipelineName = pipeline.metadata.name;
  const match = pipelineName.match(/-(deploy|restart|rollback|scale|update)-definition-tcl$/);
  const action = match ? match[1] : 'run';

  // 组件名称从 Entity 获取，不从 Pipeline 名称解析
  const component = componentName || 'unknown';

  // 显示名称格式: {action}-{component}
  const displayName = `${action}-${component}`;

  return {
    name: pipeline.metadata.name,
    displayName,
    action,
    component,
    namespace: pipeline.metadata.namespace,
    ...gitInfo,
    applicationName,
    params: pipeline.spec.params || [],
    createdAt: pipeline.metadata.creationTimestamp,
    lastRun: lastRun
      ? {
        name: lastRun.metadata.name,
        status: getRunStatus(lastRun),
        startTime: lastRun.status?.startTime || lastRun.metadata.creationTimestamp,
        completionTime: lastRun.status?.completionTime,
      }
      : undefined,
  };
};

// ArgoCD 同步选项类型
interface SyncOptions {
  prune: boolean;
  dryRun: boolean;
  applyOnly: boolean;
  force: boolean;
}

// Pipeline 执行对话框组件
interface PipelineExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  pipeline: TektonPipeline;
  displayName: string; // 显示名称，格式: {action}-{component}
  action: string; // Pipeline 动作类型，如 deploy, restart, rollback 等
  onExecute: (params: Record<string, string>, syncOptions: SyncOptions) => Promise<void>;
}

const PipelineExecutionDialog: React.FC<PipelineExecutionDialogProps> = ({
  open,
  onClose,
  pipeline,
  displayName,
  action,
  onExecute,
}) => {
  const classes = useLocalStyles();
  const { entity } = useEntity(); // 获取 Entity 信息
  const [params, setParams] = useState<Record<string, string>>({});
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string>('');

  // ArgoCD 同步选项状态
  const [syncOptions, setSyncOptions] = useState({
    prune: false,
    dryRun: false,
    applyOnly: false,
    force: false,
  });

  // 获取刷新上下文
  const { registerArgoCDRefresh, unregisterArgoCDRefresh, argoCDRefreshCounter } = useTektonRefresh();

  // 获取 ArgoCD 配置
  const { instances } = useArgocdConfig();
  const annotations = entity?.metadata?.annotations || {};
  const argoCDAppName = annotations['argocd/app-name'];
  const argoCDInstanceName = instances?.[0]?.name || 'default';
  // 从 annotation 获取 ArgoCD Application 命名空间，默认 'argocd'
  const argoCDAppNamespace = annotations['argocd/app-namespace'] || 'argocd';

  // 获取 ArgoCD 应用的实时参数
  const {
    params: argoCDParams,
    loading: argoCDLoading,
    error: argoCDError,
    refresh: refreshArgoCDParams,
  } = useArgoCDAppParams({
    appName: argoCDAppName || '',
    appNamespace: argoCDAppNamespace,
    instanceName: argoCDInstanceName,
    autoRefresh: false,
  });

  // 注册 ArgoCD 刷新回调，当 Pipeline 执行完成后会触发刷新
  useEffect(() => {
    registerArgoCDRefresh(refreshArgoCDParams);
    return () => {
      unregisterArgoCDRefresh(refreshArgoCDParams);
    };
  }, [registerArgoCDRefresh, unregisterArgoCDRefresh, refreshArgoCDParams]);

  // 当 argoCDRefreshCounter 变化时，刷新 ArgoCD 参数
  useEffect(() => {
    if (argoCDRefreshCounter > 0) {
      refreshArgoCDParams();
    }
  }, [argoCDRefreshCounter, refreshArgoCDParams]);

  // 初始化参数值
  useEffect(() => {
    if (open && pipeline.spec.params) {
      const annotations = entity?.metadata?.annotations || {};
      const initialParams: Record<string, string> = {};

      pipeline.spec.params.forEach(param => {
        let value = '';

        // 1. 从 ArgoCD 实时参数获取
        if (param.name === 'replicas' && argoCDParams?.currentReplicas !== undefined) {
          value = argoCDParams.currentReplicas.toString();
        }
        else if (param.name === 'image-tag' && argoCDParams?.currentImageTag) {
          value = argoCDParams.currentImageTag;
        }
        else if (param.name === 'history-id' && argoCDParams?.historyRevisions && argoCDParams.historyRevisions.length > 1) {
          // 对于回滚，默认选择上一个版本（倒数第二个历史记录）
          // historyRevisions 按时间升序排列，最后一个是当前版本，倒数第二个是上一个版本
          const previousHistory = argoCDParams.historyRevisions[argoCDParams.historyRevisions.length - 2];
          value = previousHistory.id;
        }
        else if (param.name === 'resource-name' && argoCDParams?.restartableResources && argoCDParams.restartableResources.length > 0) {
          // 对于重启，默认选择第一个可重启资源的名称
          value = argoCDParams.restartableResources[0].name;
        }
        else if (param.name === 'resource-kind' && argoCDParams?.restartableResources && argoCDParams.restartableResources.length > 0) {
          // 对于重启，默认选择第一个可重启资源的类型
          value = argoCDParams.restartableResources[0].kind;
        }
        // 2. 对于配置类参数，从 Entity annotations 获取（这些是 Component 固有的配置）
        else if (param.name === 'app-name' && annotations['argocd/app-name']) {
          value = annotations['argocd/app-name'];
        }
        // project-name 优先从 argocd/project 读取（避免与 Roadie 插件冲突），其次 argocd/project-name，最后从 spec.system 读取
        else if (param.name === 'project-name') {
          if (annotations['argocd/project']) {
            value = annotations['argocd/project'];
          } else if (annotations['argocd/project-name']) {
            value = annotations['argocd/project-name'];
          } else if (entity?.spec?.system) {
            // 从 spec.system 提取 system 名称
            const systemRef = typeof entity.spec.system === 'string'
              ? entity.spec.system
              : '';
            // 提取最后一部分作为 project-name
            value = systemRef.includes('/')
              ? systemRef.split('/').pop() || ''
              : systemRef;
          }
        }
        else if (param.name === 'manifest-repo' && annotations['argocd/manifest-repo']) {
          value = annotations['argocd/manifest-repo'];
        }
        else if (param.name === 'manifest-path' && annotations['argocd/manifest-path']) {
          value = annotations['argocd/manifest-path'];
        }
        else if (param.name === 'dest-server' && annotations['argocd/dest-server']) {
          value = annotations['argocd/dest-server'];
        }
        else if (param.name === 'dest-namespace' && annotations['argocd/dest-namespace']) {
          value = annotations['argocd/dest-namespace'];
        }
        // 如果没有配置 dest-namespace，尝试从 kubernetes namespace 注解获取
        else if (param.name === 'dest-namespace' && annotations['backstage.io/kubernetes-namespace']) {
          value = annotations['backstage.io/kubernetes-namespace'];
        }
        // 3. 使用默认值
        else if (param.default) {
          // 处理 default 可能是数组的情况
          value = Array.isArray(param.default) ? param.default[0] || '' : param.default;
        }

        initialParams[param.name] = value;
      });

      setParams(initialParams);
    }
  }, [open, pipeline.metadata.name, pipeline.metadata.namespace, pipeline.spec.params, entity, argoCDParams]);

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setError('');

      // 去除所有参数值的首尾空格
      const trimmedParams: Record<string, string> = {};
      Object.keys(params).forEach(key => {
        trimmedParams[key] = params[key]?.trim() || '';
      });

      // 验证必填参数（没有默认值的参数视为必填）
      if (pipeline.spec.params) {
        const missingParams: string[] = [];
        pipeline.spec.params.forEach(param => {
          // 如果参数没有默认值，且用户没有填写，则视为缺失
          if (!param.default && (!trimmedParams[param.name] || trimmedParams[param.name] === '')) {
            missingParams.push(param.name);
          }
        });

        if (missingParams.length > 0) {
          setError(`请填写必填参数: ${missingParams.join(', ')}`);
          setExecuting(false);
          return;
        }
      }

      // 执行 Pipeline（使用去除空格后的参数和同步选项）
      await onExecute(trimmedParams, syncOptions);

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '执行失败');
    } finally {
      setExecuting(false);
    }
  };

  const handleParamChange = (paramName: string, value: string) => {
    setParams(prev => ({ ...prev, [paramName]: value }));
  };

  // 检查参数是否从 Entity annotations/spec 自动填充
  const isAutoFilledFromEntity = (paramName: string) => {
    const annotations = entity?.metadata?.annotations || {};
    const argoCDParams = ['app-name', 'project-name', 'manifest-repo', 'manifest-path', 'dest-server', 'dest-namespace'];

    if (!argoCDParams.includes(paramName)) {
      return false;
    }

    // 检查是否有对应的 annotation 或 spec 字段
    if (paramName === 'app-name' && annotations['argocd/app-name']) return true;
    // project-name 可以从 argocd/project、argocd/project-name 或 spec.system 获取
    if (paramName === 'project-name' && (annotations['argocd/project'] || annotations['argocd/project-name'] || entity?.spec?.system)) return true;
    if (paramName === 'manifest-repo' && annotations['argocd/manifest-repo']) return true;
    if (paramName === 'manifest-path' && annotations['argocd/manifest-path']) return true;
    if (paramName === 'dest-server' && annotations['argocd/dest-server']) return true;
    if (paramName === 'dest-namespace' && (annotations['argocd/dest-namespace'] || annotations['backstage.io/kubernetes-namespace'])) return true;

    return false;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { overflow: 'hidden', paddingTop: 0, margin: 0 } }}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            执行 Pipeline: {displayName}
          </Typography>
          <Button
            color="inherit"
            onClick={onClose}
            startIcon={<CloseIcon />}
            size="small"
          >
            关闭
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.parameterForm}>
        {/* 显示自动填充的 ArgoCD 配置信息 */}
        <Box mb={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              ArgoCD 配置信息
            </Typography>
            {argoCDAppName && (
              <Tooltip title="刷新 ArgoCD 实时参数">
                <IconButton
                  size="small"
                  onClick={refreshArgoCDParams}
                  disabled={argoCDLoading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            {params['app-name'] && (
              <Typography variant="body2" color="textSecondary">
                <strong>应用名称:</strong> {params['app-name']}
              </Typography>
            )}
            {params['project-name'] && (
              <Typography variant="body2" color="textSecondary">
                <strong>项目名称:</strong> {params['project-name']}
              </Typography>
            )}
            {params['dest-server'] && (
              <Typography variant="body2" color="textSecondary">
                <strong>目标集群:</strong> {params['dest-server']}
              </Typography>
            )}
            {params['dest-namespace'] && (
              <Typography variant="body2" color="textSecondary">
                <strong>目标命名空间:</strong> {params['dest-namespace']}
              </Typography>
            )}
          </Box>
          {params['manifest-repo'] && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                <strong>manifest 仓库:</strong> {params['manifest-repo']}
              </Typography>
            </Box>
          )}
          {params['manifest-path'] && (
            <Typography variant="body2" color="textSecondary">
              <strong>manifest 路径:</strong> {params['manifest-path']}
            </Typography>
          )}

          {/* 显示 ArgoCD 实时参数 */}
          {argoCDParams && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginTop: 8 }}>
                实时运行参数
              </Typography>
              <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                {argoCDParams.currentImageTag && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>当前镜像 Tag:</strong> {argoCDParams.currentImageTag}
                  </Typography>
                )}
                {argoCDParams.currentReplicas !== undefined && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>当前副本数:</strong> {argoCDParams.currentReplicas}
                  </Typography>
                )}
                {argoCDParams.healthStatus && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>健康状态:</strong>{' '}
                    <Chip
                      label={argoCDParams.healthStatus}
                      size="small"
                      style={{
                        backgroundColor: argoCDParams.healthStatus === 'Healthy' ? '#4caf50' : '#ff9800',
                        color: 'white',
                        height: 20,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Typography>
                )}
                {argoCDParams.syncStatus && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>同步状态:</strong>{' '}
                    <Chip
                      label={argoCDParams.syncStatus}
                      size="small"
                      style={{
                        backgroundColor: argoCDParams.syncStatus === 'Synced' ? '#4caf50' : '#2196f3',
                        color: 'white',
                        height: 20,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Typography>
                )}
              </Box>

              {/* 显示历史版本（用于回滚） */}
              {argoCDParams.historyRevisions && argoCDParams.historyRevisions.length > 0 && (
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>可回滚版本:</strong>{' '}
                    <Tooltip title={
                      <div>
                        {argoCDParams.historyRevisions.slice(-5).reverse().map(rev => (
                          <div key={rev.id}>
                            ID: {rev.id} | Revision: {rev.revision.substring(0, 7)} | {new Date(rev.deployedAt).toLocaleString()}
                          </div>
                        ))}
                      </div>
                    }>
                      <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', cursor: 'pointer' }} />
                    </Tooltip>
                    {' '}共 {argoCDParams.historyRevisions.length} 个版本
                  </Typography>
                </Box>
              )}

              {/* 显示可重启资源（用于重启） */}
              {argoCDParams.restartableResources && argoCDParams.restartableResources.length > 0 && (
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>可重启资源:</strong>{' '}
                    <Tooltip title={
                      <div>
                        {argoCDParams.restartableResources.map(resource => (
                          <div key={`${resource.kind}/${resource.name}`}>
                            {resource.kind}: {resource.name} {resource.replicas !== undefined ? `(副本: ${resource.replicas})` : ''}
                          </div>
                        ))}
                      </div>
                    }>
                      <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', cursor: 'pointer' }} />
                    </Tooltip>
                    {' '}共 {argoCDParams.restartableResources.length} 个资源
                  </Typography>
                </Box>
              )}
            </>
          )}

          {argoCDLoading && (
            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="textSecondary">
                正在获取 ArgoCD 实时参数...
              </Typography>
            </Box>
          )}

          {argoCDError && (
            <Box mt={1}>
              <Alert severity="info" icon={false}>
                {argoCDError.message?.includes('not found') || argoCDError.message?.includes('500')
                  ? '💡 ArgoCD 应用尚未创建，首次部署后将显示实时参数'
                  : `无法获取 ArgoCD 实时参数: ${argoCDError.message}`
                }
              </Alert>
            </Box>
          )}
        </Box>

        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* 部署选项 - 对应 ArgoCD Sync Options，仅在 deploy pipeline 中显示 */}
        {action === 'deploy' && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              部署选项
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={syncOptions.prune}
                    onChange={(e) => setSyncOptions(prev => ({ ...prev, prune: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Tooltip title="删除不在 Git 中定义的资源">
                    <Typography variant="body2">PRUNE</Typography>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={syncOptions.dryRun}
                    onChange={(e) => setSyncOptions(prev => ({ ...prev, dryRun: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Tooltip title="模拟执行，不实际应用更改">
                    <Typography variant="body2">DRY RUN</Typography>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={syncOptions.applyOnly}
                    onChange={(e) => setSyncOptions(prev => ({ ...prev, applyOnly: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Tooltip title="仅应用更改，跳过资源创建前的检查">
                    <Typography variant="body2">APPLY ONLY</Typography>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={syncOptions.force}
                    onChange={(e) => setSyncOptions(prev => ({ ...prev, force: e.target.checked }))}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Tooltip title="强制替换资源">
                    <Typography variant="body2">FORCE</Typography>
                  </Tooltip>
                }
              />
            </Box>
          </Box>
        )}

        {pipeline.spec.params && pipeline.spec.params.length > 0 ? (
          <>
            <Typography variant="h6" gutterBottom>
              参数配置
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            {pipeline.spec.params
              .filter(param => !isAutoFilledFromEntity(param.name))
              .filter(param => param.name !== 'resource-kind') // 隐藏 resource-kind 参数
              .filter(param => !param.name.startsWith('sync-option-')) // 隐藏 sync-option-* 参数，已用复选框展示
              .map(param => {
                // 特殊处理：resource-name 参数使用下拉选择框
                const isResourceNameParam = param.name === 'resource-name';
                const restartableResources = argoCDParams?.restartableResources || [];

                return (
                  <FormControl key={param.name} fullWidth className={classes.parameterField}>
                    <FormLabel component="legend">
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="body2" component="span">
                          {param.name}
                          {(!param.default || isAutoFilledFromEntity(param.name)) && (
                            <span style={{ color: '#f44336', marginLeft: 4 }}>*</span>
                          )}
                        </Typography>
                      </Box>
                    </FormLabel>

                    {isResourceNameParam && restartableResources.length > 0 ? (
                      // resource-name: 使用下拉选择框显示可重启的资源
                      <TextField
                        select
                        value={params[param.name] || ''}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          handleParamChange(param.name, selectedName);

                          // 联动更新 resource-kind
                          const selectedResource = restartableResources.find(r => r.name === selectedName);
                          if (selectedResource) {
                            handleParamChange('resource-kind', selectedResource.kind);
                          }
                        }}
                        placeholder={Array.isArray(param.default) ? param.default[0] : param.default || `请选择 ${param.name}`}
                        required={!param.default}
                        error={!param.default && (!params[param.name] || params[param.name].trim() === '')}
                        helperText={
                          param.description ||
                          `从 ArgoCD 自动检测到 ${restartableResources.length} 个可重启资源`
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        {restartableResources.map(resource => (
                          <MenuItem key={`${resource.kind}/${resource.name}`} value={resource.name}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                              <Typography variant="body2">
                                {resource.name}
                              </Typography>
                              <Chip
                                label={resource.kind}
                                size="small"
                                style={{
                                  marginLeft: 8,
                                  height: 20,
                                  fontSize: '0.7rem',
                                  backgroundColor:
                                    resource.kind === 'Deployment' ? '#2196f3' :
                                      resource.kind === 'StatefulSet' ? '#9c27b0' :
                                        '#ff9800',
                                  color: 'white',
                                }}
                              />
                              {resource.replicas !== undefined && (
                                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                                  副本: {resource.replicas}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      // 普通文本输入框
                      <TextField
                        value={params[param.name] || ''}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                        onBlur={(e) => {
                          // 失去焦点时自动去除首尾空格
                          const trimmedValue = e.target.value.trim();
                          if (trimmedValue !== e.target.value) {
                            handleParamChange(param.name, trimmedValue);
                          }
                        }}
                        placeholder={Array.isArray(param.default) ? param.default[0] : param.default || `请输入 ${param.name}`}
                        required={!param.default || isAutoFilledFromEntity(param.name)}
                        error={(!param.default || isAutoFilledFromEntity(param.name)) && (!params[param.name] || params[param.name].trim() === '')}
                        helperText={
                          param.description ||
                          (param.default ? `默认值: ${Array.isArray(param.default) ? param.default.join(', ') : param.default}` : '') ||
                          `类型: ${param.type || 'string'}`
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={isAutoFilledFromEntity(param.name)}
                        InputProps={{
                          readOnly: isAutoFilledFromEntity(param.name),
                          style: isAutoFilledFromEntity(param.name) ? {
                            backgroundColor: '#f5f5f5',
                            cursor: 'not-allowed',
                          } : undefined,
                        }}
                      />
                    )}
                  </FormControl>
                );
              })}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            此 Pipeline 无需参数
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onClose} disabled={executing}>
          取消
        </Button>
        <Button
          onClick={handleExecute}
          variant="contained"
          color="primary"
          disabled={executing || argoCDLoading}
          startIcon={executing ? <CircularProgress size={16} /> : (argoCDLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />)}
        >
          {executing ? '执行中...' : (argoCDLoading ? '加载参数...' : '执行 Pipeline')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TektonPipelineCard: React.FC<{
  pipeline: TektonPipeline;
  lastRun?: TektonPipelineRun;
  recentRuns?: TektonPipelineRun[];
}> = ({ pipeline, lastRun, recentRuns = [] }) => {
  const cardClasses = usePipelineCardStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const identityApi = useApi(identityApiRef);
  const oidcAuthApi = useApi(oidcAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { entity } = useEntity();
  const { triggerRefresh } = useTektonRefresh();

  // Owner 检查：判断当前用户是否有权限执行 Pipeline
  const { canExecute, ownerCheckDone } = useOwnershipCheck();

  // 状态管理
  const [executionDialogOpen, setExecutionDialogOpen] = useState(false);
  // 本地状态：刚刚执行了 Pipeline，用于在数据刷新前显示运行中状态
  const [justExecuted, setJustExecuted] = useState(false);
  // 刚刚执行的 PipelineRun 名称
  const [justExecutedRunName, setJustExecutedRunName] = useState<string>('');

  // 转换为显示格式，传入 Entity 的名称作为组件名称
  const displayData = transformPipeline(pipeline, lastRun, entity?.metadata?.name);

  // 获取运行状态
  const getRunStatus = (run: TektonPipelineRun): 'Succeeded' | 'Running' | 'Failed' | 'Pending' => {
    if (!run.status?.conditions || run.status.conditions.length === 0) return 'Pending';

    const succeededCondition = run.status.conditions.find(c => c.type === 'Succeeded');
    if (!succeededCondition) return 'Pending';

    // 成功
    if (succeededCondition.status === 'True') return 'Succeeded';

    // 失败
    if (succeededCondition.status === 'False') return 'Failed';

    // Unknown 状态需要进一步判断
    if (succeededCondition.status === 'Unknown') {
      // 检查 reason 字段来区分 Running 和 Pending
      const reason = succeededCondition.reason || '';
      if (reason === 'Running' || reason === 'Started') {
        return 'Running';
      }
      return 'Pending';
    }

    return 'Pending';
  };

  // 获取正在运行的 PipelineRun
  const runningPipelineRun = useMemo(() => {
    return recentRuns.find(run => {
      const status = getRunStatus(run);
      return status === 'Running' || status === 'Pending';
    });
  }, [recentRuns]);

  // 检查是否有正在运行的 PipelineRun（包括刚刚执行的本地状态）
  const hasRunningPipelineRun = !!runningPipelineRun || justExecuted;

  // 当从 API 获取到运行中的 PipelineRun 后，清除本地状态
  useEffect(() => {
    if (runningPipelineRun && justExecuted) {
      setJustExecuted(false);
      setJustExecutedRunName('');
    }
  }, [runningPipelineRun, justExecuted]);

  // 取消运行状态
  const [cancelling, setCancelling] = useState(false);

  // 取消正在运行的 PipelineRun
  const handleCancelPipelineRun = useCallback(async () => {
    if (!runningPipelineRun) return;

    try {
      setCancelling(true);

      // 获取 Tekton 集群配置
      const annotations = entity?.metadata?.annotations || {};
      const tektonClusterName = annotations['backstage.io/tekton-pipeline-cluster'] || 'tekton-control';

      // 根据 PipelineRun 的 apiVersion 确定使用的 Tekton API 版本
      const tektonApiVersion = getTektonApiVersion(runningPipelineRun.apiVersion);

      // 通过 Kubernetes API 取消 PipelineRun
      // 设置 spec.status 为 "Cancelled"
      const patchBody = {
        spec: {
          status: 'Cancelled',
        },
      };

      const response = await kubernetesApi.proxy({
        clusterName: tektonClusterName,
        path: `/apis/${tektonApiVersion}/namespaces/${runningPipelineRun.metadata.namespace}/pipelineruns/${runningPipelineRun.metadata.name}`,
        init: {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
          body: JSON.stringify(patchBody),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`取消失败: ${response.status} - ${errorText}`);
      }

      // 延迟刷新
      setTimeout(() => {
        triggerRefresh();
      }, 1000);
    } catch (err) {
      alert(`取消失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setCancelling(false);
    }
  }, [runningPipelineRun, kubernetesApi, entity, triggerRefresh]);

  // 获取状态对应的颜色
  const getStatusDotColor = (status: 'Succeeded' | 'Running' | 'Failed' | 'Pending'): string => {
    switch (status) {
      case 'Succeeded':
        return '#4caf50'; // 绿色
      case 'Running':
        return '#2196f3'; // 蓝色
      case 'Failed':
        return '#f44336'; // 红色
      case 'Pending':
        return '#9e9e9e'; // 灰色
      default:
        return '#9e9e9e';
    }
  };

  // 格式化时间
  const formatTime = (time: string) => {
    return new Date(time).toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  // 执行 Pipeline
  const handleExecutePipeline = useCallback(async (params: Record<string, string>, syncOptions: SyncOptions) => {
    try {
      // 获取配置的 Tekton 集群名称
      const targetClusterName = entity?.metadata?.annotations?.['backstage.io/tekton-pipeline-cluster'] || 'tekton-control';

      // 获取当前用户信息
      const identity = await identityApi.getBackstageIdentity();
      const userRef = identity?.userEntityRef || 'unknown';

      // 从 OIDC Profile 获取邮箱
      let userEmail: string | undefined;
      try {
        const profile = await oidcAuthApi.getProfile();
        userEmail = profile?.email;
      } catch {
        // 获取 profile 失败时忽略
      }

      // 生成唯一的 PipelineRun 名称
      // 格式: {pipelineName}-{timestamp}
      // 所有元数据（cluster, component, workflow-type, triggered-by）都通过 annotations 传递
      const componentName = entity?.metadata?.name || 'unknown';
      const displayData = transformPipeline(pipeline, undefined, componentName);
      const pipelineRunName = generatePipelineRunName(displayData.displayName);

      // 从 Entity 注解中自动填充 ArgoCD 配置参数
      const annotations = entity?.metadata?.annotations || {};
      const autoFilledParams: Record<string, string> = {
        ...params, // 用户输入的参数
      };

      // 添加 ArgoCD 同步选项参数
      if (syncOptions.prune) {
        autoFilledParams['sync-option-prune'] = 'true';
      }
      if (syncOptions.dryRun) {
        autoFilledParams['sync-option-dry-run'] = 'true';
      }
      if (syncOptions.applyOnly) {
        autoFilledParams['sync-option-apply-only'] = 'true';
      }
      if (syncOptions.force) {
        autoFilledParams['sync-option-force'] = 'true';
      }

      // 自动填充 ArgoCD 配置（如果 Pipeline 需要这些参数）
      const pipelineParamNames = pipeline.spec.params?.map(p => p.name) || [];

      // 1. 填充 ArgoCD 应用配置参数（不带前缀）
      if (pipelineParamNames.includes('app-name') && annotations['argocd/app-name']) {
        autoFilledParams['app-name'] = annotations['argocd/app-name'];
      }
      // project-name 优先从 argocd/project 读取（避免与 Roadie 插件冲突）
      if (pipelineParamNames.includes('project-name')) {
        let projectName = '';
        if (annotations['argocd/project']) {
          projectName = annotations['argocd/project'];
        } else if (annotations['argocd/project-name']) {
          projectName = annotations['argocd/project-name'];
        }

        // 安全验证：确保 project-name 与 Component 的 System 关联的 ArgoCD Project 一致
        if (projectName && entity?.spec?.system) {
          try {
            const systemName = typeof entity.spec.system === 'string'
              ? entity.spec.system.split('/').pop() || entity.spec.system
              : '';
            
            if (systemName) {
              // 获取 System 实体
              const systemEntity = await catalogApi.getEntityByRef(`system:default/${systemName}`);
              
              if (systemEntity) {
                // 从 System 的 dependsOn 获取 ArgoCD Project Resource
                const dependsOn = systemEntity.spec?.dependsOn || [];
                let expectedProjectName = '';
                
                for (const dep of dependsOn) {
                  if (typeof dep === 'string' && dep.startsWith('resource:default/argocd-')) {
                    const resourceName = dep.replace('resource:default/', '');
                    const projectResource = await catalogApi.getEntityByRef(`resource:default/${resourceName}`);
                    
                    if (projectResource) {
                      const realProjectName = projectResource.metadata?.annotations?.['argocd.argoproj.io/project-name'];
                      if (realProjectName) {
                        expectedProjectName = realProjectName;
                        break;
                      }
                    }
                  }
                }
                
                // 验证 project-name 是否匹配
                if (expectedProjectName && projectName !== expectedProjectName) {
                  throw new Error(
                    `安全错误：Component 的 argocd/project 注解 '${projectName}' 与 System '${systemName}' 关联的 ArgoCD Project '${expectedProjectName}' 不匹配。` +
                    `此注解由系统管理，不能手动修改。请使用 Scaffolder 模板更新 Component。`
                  );
                }
              }
            }
          } catch (error) {
            // 如果验证失败，阻止 Pipeline 执行
            if (error instanceof Error && error.message.includes('安全错误')) {
              throw error;
            }
            // 其他错误（如 System 不存在）记录警告但不阻止
            console.warn('ArgoCD Project 安全验证失败:', error);
          }
        }

        if (projectName) {
          autoFilledParams['project-name'] = projectName;
        }
      }
      if (pipelineParamNames.includes('manifest-repo') && annotations['argocd/manifest-repo']) {
        autoFilledParams['manifest-repo'] = annotations['argocd/manifest-repo'];
      }
      if (pipelineParamNames.includes('manifest-path') && annotations['argocd/manifest-path']) {
        autoFilledParams['manifest-path'] = annotations['argocd/manifest-path'];
      }
      if (pipelineParamNames.includes('dest-server') && annotations['argocd/dest-server']) {
        autoFilledParams['dest-server'] = annotations['argocd/dest-server'];
      }
      if (pipelineParamNames.includes('dest-namespace') && annotations['argocd/dest-namespace']) {
        autoFilledParams['dest-namespace'] = annotations['argocd/dest-namespace'];
      }

      // 如果没有配置 dest-namespace，尝试从 kubernetes namespace 注解获取
      if (pipelineParamNames.includes('dest-namespace') && !autoFilledParams['dest-namespace']) {
        const k8sNamespace = annotations['backstage.io/kubernetes-namespace'];
        if (k8sNamespace) {
          autoFilledParams['dest-namespace'] = k8sNamespace;
        }
      }

      // 注意：用户输入的参数名保持简洁（如 image-tag, replicas）
      // Pipeline 会在 tasks[].params 层自动重命名为 ARGOCD_ENV_ 前缀

      // 处理参数：支持 string 和 array 类型
      const pipelineRunParams = Object.entries(autoFilledParams).map(([name, value]) => {
        // 检查 Pipeline 中该参数的类型
        const paramDef = pipeline.spec.params?.find(p => p.name === name);

        if (paramDef?.type === 'array') {
          // Array 类型参数
          // 如果 value 是字符串，尝试解析为数组
          let arrayValue: string[];
          if (typeof value === 'string') {
            try {
              // 尝试 JSON 解析
              arrayValue = JSON.parse(value);
            } catch {
              // 如果不是 JSON，按逗号分割
              arrayValue = value.split(',').map(v => v.trim());
            }
          } else if (Array.isArray(value)) {
            arrayValue = value;
          } else {
            arrayValue = [String(value)];
          }

          return { name, value: arrayValue };
        } else {
          // String 类型参数
          return { name, value: String(value) };
        }
      });

      // 使用 Pipeline 相同的 API 版本创建 PipelineRun（动态检测，支持 v1 和 v1beta1）
      const tektonApiVersion = pipeline.apiVersion || 'tekton.dev/v1';
      const apiVersionPath = tektonApiVersion.includes('v1beta1') ? 'v1beta1' : 'v1';

      // 简化 PipelineRun 配置，与自定义工作流保持一致
      const pipelineRunObject = {
        apiVersion: tektonApiVersion,
        kind: 'PipelineRun',
        metadata: {
          name: pipelineRunName,
          namespace: pipeline.metadata.namespace,
          annotations: {
            // 使用 backstage.io/* 前缀，避免被 Tekton Results Watcher 覆盖
            // watcher patch 会将这些 annotations 复制到 Result
            'backstage.io/cluster': targetClusterName,
            'backstage.io/component': componentName,
            'backstage.io/workflow-type': WORKFLOW_TYPE.CD,
            'backstage.io/triggered-by': userEmail || userRef,
          },
        },
        spec: {
          pipelineRef: {
            name: pipeline.metadata.name,
          },
          params: pipelineRunParams,
        },
      };

      // 调用后端 pipeline-management API（包含权限校验 + destinations 白名单）
      const { token } = await identityApi.getCredentials();
      const scaffolderBaseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const componentRef = `component:${entity?.metadata?.namespace || 'default'}/${entity?.metadata?.name}`;

      const response = await fetchApi.fetch(
        `${scaffolderBaseUrl}/pipeline-management/trigger`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            componentRef,
            tektonCluster: targetClusterName,
            tektonNamespace: pipeline.metadata.namespace,
            pipelineRun: pipelineRunObject,
          }),
        },
      );

      if (response.ok) {
        const responseData = await response.json();

        // 立即设置本地状态为运行中，不用等待数据刷新
        setJustExecuted(true);
        setJustExecutedRunName(pipelineRunName);

        // 延迟 1 秒后触发刷新，让 K8s 有时间创建资源
        setTimeout(() => {
          triggerRefresh();
        }, 1000);

        return responseData;
      }

      // 尝试获取错误详情
      let errorDetails = '';
      try {
        const errorResponse = await response.json();
        errorDetails = JSON.stringify(errorResponse, null, 2);
      } catch {
        errorDetails = await response.text();
      }

      const errorMessage = `创建 PipelineRun 失败 (${response.status} ${response.statusText}): ${errorDetails}`;
      throw new Error(errorMessage);
    } catch (error) {
      throw error;
    }
  }, [kubernetesApi, identityApi, oidcAuthApi, pipeline, entity, triggerRefresh, discoveryApi, fetchApi]);

  const handleExecute = () => {
    setExecutionDialogOpen(true);
  };

  // 计算运行时长
  const getRunDuration = (run: TektonPipelineRun): string => {
    const startTime = run.status?.startTime || run.metadata.creationTimestamp;
    const endTime = run.status?.completionTime || new Date().toISOString();

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const durationMs = end - start;

    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}分${seconds}秒`;
    }
    return `${seconds}秒`;
  };

  // 检查是否运行超时（超过 30 分钟视为异常）
  const isRunningTooLong = (run: TektonPipelineRun): boolean => {
    const status = getRunStatus(run);
    if (status !== 'Running') return false;

    const startTime = run.status?.startTime || run.metadata.creationTimestamp;
    const now = new Date().getTime();
    const start = new Date(startTime).getTime();
    const durationMs = now - start;

    // 30 分钟 = 1800000 毫秒
    return durationMs > 1800000;
  };

  // 准备最近5次运行状态的圆点
  const renderStatusDots = () => {
    const dots = [];
    const maxDots = 5;

    // 获取最近5次运行
    const runs = recentRuns.slice(0, maxDots);

    for (let i = 0; i < maxDots; i++) {
      if (i < runs.length) {
        const status = getRunStatus(runs[i]);
        const isTooLong = isRunningTooLong(runs[i]);
        const duration = getRunDuration(runs[i]);

        // 如果运行时间过长，使用橙色警告
        let color = getStatusDotColor(status);
        if (isTooLong) {
          color = '#ff9800'; // 橙色警告
        }

        const tooltipText = `${runs[i].metadata.name}\n状态: ${status}\n时长: ${duration}${isTooLong ? '\n⚠️ 运行时间过长' : ''}`;

        dots.push(
          <span
            key={i}
            className={cardClasses.statusDot}
            style={{
              backgroundColor: color,
              ...(isTooLong && {
                border: '2px solid #ff9800',
              })
            }}
            title={tooltipText}
          />
        );
      } else {
        // 没有运行记录时显示灰色圆点
        dots.push(
          <span
            key={i}
            className={cardClasses.statusDot}
            style={{ backgroundColor: '#e0e0e0' }}
            title="无记录"
          />
        );
      }
    }

    return <Box className={cardClasses.statusDots}>{dots}</Box>;
  };

  return (
    <Card className={cardClasses.card}>
      <CardHeader
        className={cardClasses.cardHeader}
        title={
          <Typography className={cardClasses.cardTitle} title={displayData.name}>
            {displayData.displayName}
          </Typography>
        }
        subheader={
          <Box className={cardClasses.statusDots} mt={0.5}>
            {renderStatusDots()}
          </Box>
        }
      />

      <CardContent className={cardClasses.cardContent}>
        <Box className={cardClasses.infoSection}>
          {/* 参数信息 */}
          <Box className={cardClasses.infoLine}>
            <Typography variant="caption" color="textSecondary">
              参数: {displayData.params.length} 个
            </Typography>
          </Box>

          {/* 创建时间 */}
          <Box className={cardClasses.infoLine}>
            <ScheduleIcon style={{ fontSize: 14 }} />
            <Typography variant="caption" color="textSecondary">
              创建: {formatTime(displayData.createdAt)}
            </Typography>
          </Box>

          {/* 最近运行时间 */}
          {displayData.lastRun && (
            <Box className={cardClasses.infoLine}>
              <ScheduleIcon style={{ fontSize: 14 }} />
              <Typography variant="caption" color="textSecondary">
                最近: {formatTime(displayData.lastRun.startTime)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* 操作按钮 */}
        <Box className={cardClasses.actionButtons}>
          {hasRunningPipelineRun ? (
            // 运行中显示取消按钮
            <>
              <Tooltip title={`正在运行: ${runningPipelineRun?.metadata.name || justExecutedRunName}`}>
                <Box className={cardClasses.runningStatus}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">运行中</Typography>
                </Box>
              </Tooltip>
              <Tooltip title={justExecuted && !runningPipelineRun ? '等待数据同步...' : '取消运行'}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    className={cardClasses.cancelButton}
                    startIcon={cancelling ? <CircularProgress size={14} /> : <StopIcon />}
                    onClick={handleCancelPipelineRun}
                    disabled={cancelling || (justExecuted && !runningPipelineRun)}
                  >
                    {cancelling ? '取消中' : '取消'}
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            // 未运行显示执行按钮
            <Tooltip title={canExecute ? "执行 Pipeline" : "无权限：您不是此组件的 Owner"}>
              <span style={{ width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  fullWidth
                  className={cardClasses.executeButton}
                  startIcon={<PlayArrowIcon />}
                  onClick={handleExecute}
                  disabled={!canExecute || !ownerCheckDone}
                >
                  {!ownerCheckDone ? '检查中...' : (canExecute ? '执行' : '无权限')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </CardContent>

      {/* Pipeline 执行对话框 */}
      {executionDialogOpen && (
        <PipelineExecutionDialog
          open={executionDialogOpen}
          onClose={() => setExecutionDialogOpen(false)}
          pipeline={pipeline}
          displayName={displayData.displayName}
          action={displayData.action}
          onExecute={handleExecutePipeline}
        />
      )}
    </Card>
  );
};

export const TektonPipelineCards: React.FC = () => {
  const localClasses = useLocalStyles();
  const { entity } = useEntity();
  const { refreshCounter } = useTektonRefresh();

  // 获取指定的 Tekton Pipeline 集群名称，默认为 'tekton-control'
  const tektonClusterName = entity?.metadata?.annotations?.['backstage.io/tekton-pipeline-cluster'] || 'tekton-control';

  // 获取 Tekton 命名空间
  const tektonNamespace = entity?.metadata?.annotations?.['backstage.io/tekton-namespace'] ||
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ||
    'default';

  const componentName = entity?.metadata?.name || '';

  // 使用统一的 useTektonPipelines Hook 获取 Pipeline 和 PipelineRuns
  const {
    pipelines,
    pipelineRuns: resultsRuns,
    pipelinesWithRuns,
    loading,
    error,
    refresh,
  } = useTektonPipelines({
    componentName,
    clusterName: tektonClusterName,
    namespace: tektonNamespace,
    workflowType: WORKFLOW_TYPE.CD,
    pollInterval: 6000,
    enablePolling: !!componentName && !!tektonNamespace,
  });

  // 当 refreshCounter 变化时，立即刷新
  useEffect(() => {
    if (refreshCounter > 0) {
      refresh();
    }
  }, [refreshCounter, refresh]);

  // 只在首次加载（没有任何数据）时显示加载遮罩
  // 如果已有数据，则静默刷新，不显示遮罩
  if (loading && pipelines.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 16 }}>
          加载 Tekton Pipelines...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        加载 Pipeline 数据失败: {typeof error === 'string' ? error : 'Unknown error'}
      </Alert>
    );
  }

  if (pipelinesWithRuns.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" align="center" color="textSecondary" gutterBottom>
            没有找到相关的 Tekton Pipeline
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" gutterBottom>
            请检查 entity 是否配置了正确的 Tekton 注解
          </Typography>
          <Box p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <Typography variant="caption" color="textSecondary">
              调试信息:<br />
              • Entity: {entity?.metadata?.name}<br />
              • 目标集群: {tektonClusterName}<br />
              • 目标命名空间: {tektonNamespace}<br />
              • 找到的Pipeline数: {pipelines.length}<br />
              • Results API PipelineRun数: {resultsRuns.length}<br />
              • Kubernetes注解: {entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] || '未配置'}<br />
              • Tekton注解: {entity?.metadata?.annotations?.['tekton.dev/cicd'] || '未配置'}<br />
              • Tekton命名空间: {entity?.metadata?.annotations?.['backstage.io/tekton-namespace'] || '未配置'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Pipelines ({pipelinesWithRuns.length})
      </Typography>

      <Grid container spacing={3}>
        {pipelinesWithRuns.map(({ pipeline, lastRun, recentRuns }) => (
          <Grid item xs={12} sm={6} md={4} key={`${pipeline.metadata.namespace}/${pipeline.metadata.name}`}>
            <TektonPipelineCard pipeline={pipeline} lastRun={lastRun} recentRuns={recentRuns} />
          </Grid>
        ))}
      </Grid>

      {/* Pipeline Runs 表格 - 使用共享组件 */}
      <Box className={localClasses.runsSection}>
        <PipelineRunsTable
          runs={resultsRuns}
          clusterName={tektonClusterName}
          pipelineNames={pipelines.map(p => p.metadata.name)}
          showFilters={true}
          showPagination={true}
          showRerunButton={false}
        />
      </Box>
    </Box>
  );
};
